<template>
  <header
    v-if="isMobile"
    ref="mobileHeader"
    class="mobile-header"
  >
    <div class="wrapper">
      <picture class="logo">
        <source
          type="image/webp"
          srcset="@/assets/img/expirenza-logo.webp 1x, @/assets/img/expirenza-logo@2x.webp 2x"
        />
        <source
          type="image/png"
          srcset="@/assets/img/expirenza-logo.png 1x, @/assets/img/expirenza-logo@2x.png 2x"
        />
        <img
          :data-testid="dataTestId.sidebar.mobileLogo"
          src="@/assets/img/expirenza-logo.png"
          srcset="@/assets/img/expirenza-logo.png 1x, @/assets/img/expirenza-logo@2x.png 2x"
          loading="lazy"
          alt="expirenza"
        />
      </picture>
      <div
        class="burger-button"
        :data-testid="dataTestId.sidebar.mobileBurgerMenu"
      >
        <img
          src="@/assets/icons/menu.svg"
          alt="burder"
          width="24"
          @click="toggleSidebarMenu"
        />
      </div>
      <div
        v-if="route.fullPath !== '/'"
        class="restaurant-slot"
        :data-testid="dataTestId.sidebar.mobileRestaurantLogo"
      >
        <img
          v-if="restaurant.logoURL"
          :src="restaurant.logoURL"
          :alt="restaurant.title"
        />
        <p class="body-text-l strong">{{ restaurant.title }}</p>
      </div>
      <div
        class="auth-button"
        @click="handleClick"
      >
        <p class="body-text-s strong">
          {{ isLoggedIn ? getUserInfo.phone : $t("auth.loginButton") }}
        </p>
        <img
          src="@/assets/icons/sign-out.svg"
          alt="sign-out"
          width="24"
        />
      </div>
    </div>
  </header>

  <header
    v-else
    class="sidebar"
  >
    <SideBarContent />
  </header>

  <main>
    <slot />
  </main>

  <!-- sidebar from burger menu -->
  <BaseDrawer
    v-model="sidebarVisible"
    blockScroll
    position="left"
  >
    <template #container>
      <SideBarContent />
    </template>
  </BaseDrawer>
</template>
<script setup lang="ts">
import {storeToRefs} from "pinia"
import {useRouter, useRoute} from "vue-router"
import {RoutesEnum} from "@/router/types"
import {useRestaurantStore} from "@/stores/restaurant"
import {useAuthStore} from "@/stores/auth"
import {usePageStore} from "@/stores/page"
import dataTestId from "@/constants/dataTestId"
// components
import SideBarContent from "@/components/SideBarContent.vue"
import BaseDrawer from "@/components/BaseDrawer.vue"

const restaurantStore = useRestaurantStore()
const {restaurantSideBarInfo: restaurant} = storeToRefs(restaurantStore)
const authStore = useAuthStore()
const {getUserInfo, isLoggedIn} = storeToRefs(authStore)

const router = useRouter()
const route = useRoute()
function handleClick() {
  if (isLoggedIn.value) {
    authStore.logout()
  } else {
    router.push(RoutesEnum.UserLogin)
  }
}
const pageStore = usePageStore()
const {isMobile, sidebarVisible, mobileHeader} = storeToRefs(pageStore)

function toggleSidebarMenu() {
  pageStore.$patch({
    sidebarVisible: !sidebarVisible.value,
  })
}

pageStore.watchRestaurantTitle(() => restaurant.value.title)
pageStore.initializeResizeObserver()
</script>
<style scoped lang="less">
@import "@/assets/variables.less";
.mobile-header {
  width: 100%;
  background: var(--static-white);
  box-shadow: 0px 1px 6px 0px #0000000d;
  position: fixed;
  top: 0;
  left: 0;
  z-index: 999;
  display: block;
  @media (min-width: @mobile-max-width) {
    display: none;
  }
  .wrapper {
    padding: 12px 16px;
    display: grid;
    grid-template-columns: 24px auto 24px;
    grid-template-areas: "logo restaurant auth";
    align-items: center;
    justify-items: center;
    gap: 16px;
    max-width: 1432px;
    margin: 0 auto;
    @media (min-width: @mobile-max-width) {
      grid-template-columns: 160px auto 164px;
      padding: 8px 32px;
      gap: 16px;
    }
    @media (min-width: @header-tablet-max-width) {
      grid-template-columns: 160px auto 164px;
      padding: 8px 150px;
      gap: 16px;
    }
  }
  .logo {
    grid-area: logo;
    display: none;
    @media (min-width: @mobile-max-width) {
      display: block;
    }
  }
  .burger-button {
    grid-area: logo;
    height: 24px;
    @media (min-width: @mobile-max-width) {
      display: none;
    }
  }
  .restaurant-slot {
    grid-area: restaurant;
    display: flex;
    align-items: center;
    gap: 12px;
    @media (min-width: @mobile-max-width) {
      gap: 8px;
    }
    img {
      width: 24px;
      height: 24px;
      border-radius: 50%;
    }
  }
  .auth-button {
    grid-area: auth;
    display: none;
    color: var(--fg-default);
    cursor: pointer;
    @media (min-width: @mobile-max-width) {
      display: flex;
      align-items: center;
      gap: 8px;
    }
  }
}
.sidebar {
  height: 100%;
  width: var(--sidebar-width);
  position: fixed;
  z-index: 1;
  top: 0;
  left: 0;
  background-color: var(--p-neutral-100);
  overflow-x: hidden;
  border-right: 1px solid var(--border-color-default);
}
main {
  padding-top: var(--mobile-header-height, 48px);
  margin-left: unset;
  height: 100%;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  @media (min-width: @mobile-max-width) {
    padding-top: 0;
    margin-left: var(--sidebar-width);
  }
}
</style>
